export const RUNTIME_SETTINGS_KEY = "constructorfleet:product:bhg-call:settings";
export const PHRASE_RULES_KEY = "constructorfleet:product:bhg-call:phrase-rules";
export const RESULT_RULES_KEY = "constructorfleet:product:bhg-call:result-rules";

const US_E164_PHONE_REGEX = /^\+1\d{10}$/;

export type BhgCallSettings = {
  voiceProvider: "twilio" | "telnyx";
  dialFromPhoneNumbers: string[];
  maxActiveCallsPerDialFromNumber: number;
  dialToPhoneNumber: string;
  maxCallDurationSeconds: number;
  webhookStatusCallbackBaseUrl: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  telnyxApiKey: string;
  telnyxPublicKey: string;
  telnyxConnectionId: string;
};

export type BhgPhraseTrigger = {
  id: string;
  phrasePatterns: string[];
  verificationPhrases: string[];
  requiredIds: string[];
  resetIdsOnInvalid: string[];
  sendDigits: string | null;
  sendDigitsWhenInvalid: string | null;
  pauseSeconds: number | null;
  recordSeconds: number | null;
  oneshot: boolean;
  hangup: boolean;
  priority: number;
};

export type BhgResultTrigger = {
  id: string;
  label: string;
  phrasePatterns: string[];
  isScheduledTrigger: boolean;
  /**
   * Whether landing on this rule satisfies the monthly drug-screen obligation.
   * A bottle check is a scheduled visit but is not a screen.
   */
  countsTowardMonthlyScreen?: boolean;
  offsetNDays?: number;
  isTechDifficultyAlert?: boolean;
  priority: number;
};

export type BhgCallAdminSettings = {
  runtimeSettings: BhgCallSettings;
  phraseRules: BhgPhraseTrigger[];
  resultRules: BhgResultTrigger[];
};

export type BhgSettingsSaveResult = { ok: true } | { ok: false; error: string };

export type BhgAdminUser = {
  id: number;
  username: string;
  role: "admin" | "user";
  isActive: boolean;
  billingStatus: "active" | "trialing" | "past_due" | "unpaid" | "canceled" | "inactive";
};

export type TriggerManualCallsResult =
  | { ok: true; queuedCount: number; initiatedCount?: number }
  | { ok: false; error: string };

export type ActiveDbCall = {
  id: number;
  userId: number;
  callSid: string;
  status: string;
  startedAt: string;
  voiceProvider: string | null;
  errorReason: string | null;
};

export type ActiveCallEntry = {
  callSid: string;
  dbCall: ActiveDbCall | null;
  providerActive: boolean;
};

export type CallHistoryEntry = {
  id: number;
  userId: number;
  startedAt: string;
  status: string;
  resultLabel: string | null;
  resultDate: string | null;
  callSid: string;
  transcript: string | null;
  recordingAvailable: boolean;
  errorReason: string | null;
  voiceProvider: string | null;
};

export type BhgManualHangupOutcome = "hangup_requested" | "already_terminal" | "not_found";

export type BhgManualHangupResult = {
  callSid: string;
  outcome: BhgManualHangupOutcome;
  persistedStatus: "completed" | "failed" | null;
  providerStatus: string | null;
};

export type ProductDbClient = {
  loadJson: <TValue>(key: string) => Promise<TValue | null>;
  saveJson: <TValue>(key: string, value: TValue) => Promise<void>;
  listUsers?: () => Promise<BhgAdminUser[]>;
  requestAdmin?: <TValue = unknown>(input: {
    method: "GET" | "POST";
    path: string;
    body?: unknown;
  }) => Promise<{ ok: true; data: TValue } | { ok: false; error: string }>;
};

export const DEFAULT_BHG_CALL_SETTINGS: BhgCallSettings = {
  voiceProvider: "twilio",
  dialFromPhoneNumbers: ["+15550184421"],
  maxActiveCallsPerDialFromNumber: 1,
  dialToPhoneNumber: "+15550000002",
  maxCallDurationSeconds: 180,
  webhookStatusCallbackBaseUrl: "",
  twilioAccountSid: "",
  twilioAuthToken: "",
  telnyxApiKey: "",
  telnyxPublicKey: "",
  telnyxConnectionId: "",
};

export const DEFAULT_BHG_PHRASE_TRIGGERS: BhgPhraseTrigger[] = [
  {
    id: "submit_patient_identifiers",
    phrasePatterns: ["enter last four", "patient number"],
    verificationPhrases: [],
    requiredIds: [],
    resetIdsOnInvalid: [],
    sendDigits: "{{last4_ssn}}{{last4_patient_number}}",
    sendDigitsWhenInvalid: null,
    pauseSeconds: null,
    recordSeconds: null,
    oneshot: false,
    hangup: false,
    priority: 10,
  },
  {
    id: "confirm_menu_selection",
    phrasePatterns: ["press 1", "for test information"],
    verificationPhrases: [],
    requiredIds: ["submit_patient_identifiers"],
    resetIdsOnInvalid: [],
    sendDigits: "1",
    sendDigitsWhenInvalid: null,
    pauseSeconds: null,
    recordSeconds: null,
    oneshot: false,
    hangup: false,
    priority: 20,
  },
];

export const DEFAULT_BHG_RESULT_TRIGGERS: BhgResultTrigger[] = [
  {
    id: "drug_screen",
    label: "Drug Screen",
    phrasePatterns: ["drug screen", "scheduled", "today"],
    isScheduledTrigger: true,
    countsTowardMonthlyScreen: true,
    priority: 10,
  },
  {
    id: "bottle_check",
    label: "Bottle Check",
    phrasePatterns: ["diversion", "check", "scheduled"],
    isScheduledTrigger: true,
    // A bottle check is a scheduled visit but is not a screen.
    countsTowardMonthlyScreen: false,
    priority: 20,
  },
  {
    id: "not_scheduled",
    label: "Not Scheduled",
    phrasePatterns: ["not required", "no screen"],
    isScheduledTrigger: false,
    countsTowardMonthlyScreen: false,
    priority: 50,
  },
];

export function normalizeStringList(values: string[]) {
  return values.map((value) => value.trim()).filter(Boolean);
}

export function normalizeDialString(value: string | null) {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized.includes("{{") && normalized.includes("}}")) {
    return normalized;
  }
  return /^[0-9#*w]+$/.test(normalized) ? normalized : null;
}

function parsePhoneList(input: unknown) {
  if (!Array.isArray(input)) {
    return null;
  }

  const values = input.map((value) => String(value ?? "").trim()).filter(Boolean);
  if (values.length === 0) {
    return null;
  }
  if (values.some((value) => !US_E164_PHONE_REGEX.test(value))) {
    return null;
  }

  return values;
}

function parseBaseUrl(input: unknown) {
  const value = String(input ?? "").trim();
  if (!value) {
    return null;
  }

  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return null;
  }
}

export function normalizeBhgCallSettings(
  input: unknown
): { ok: true; settings: BhgCallSettings } | { ok: false; error: string } {
  const payload = input as Record<string, unknown>;
  const dialFromPhoneNumbers = parsePhoneList(payload?.dialFromPhoneNumbers);
  if (!dialFromPhoneNumbers) {
    return {
      ok: false,
      error: "Dial-from phone numbers are required and must be full +1 US phone numbers.",
    };
  }

  const dialToPhoneNumber = String(payload?.dialToPhoneNumber ?? "").trim();
  if (!US_E164_PHONE_REGEX.test(dialToPhoneNumber)) {
    return {
      ok: false,
      error: "Dial-to phone number must be a full +1 US phone number.",
    };
  }

  const maxActiveCallsPerDialFromNumber = Number(payload?.maxActiveCallsPerDialFromNumber);
  if (!Number.isInteger(maxActiveCallsPerDialFromNumber) || maxActiveCallsPerDialFromNumber <= 0) {
    return {
      ok: false,
      error: "Max active calls per dial-from phone number must be a positive integer.",
    };
  }

  const maxCallDurationSeconds = Number(payload?.maxCallDurationSeconds);
  if (!Number.isInteger(maxCallDurationSeconds) || maxCallDurationSeconds <= 0) {
    return {
      ok: false,
      error: "Max call duration must be a positive integer (seconds).",
    };
  }

  const webhookStatusCallbackBaseUrl = parseBaseUrl(payload?.webhookStatusCallbackBaseUrl);
  if (!webhookStatusCallbackBaseUrl) {
    return {
      ok: false,
      error: "Webhook status callback base URL must be a valid HTTP(S) URL.",
    };
  }

  const voiceProviderRaw = String(payload?.voiceProvider ?? "")
    .trim()
    .toLowerCase();

  return {
    ok: true,
    settings: {
      voiceProvider: voiceProviderRaw === "telnyx" ? ("telnyx" as const) : ("twilio" as const),
      dialFromPhoneNumbers,
      maxActiveCallsPerDialFromNumber,
      dialToPhoneNumber,
      maxCallDurationSeconds,
      webhookStatusCallbackBaseUrl,
      twilioAccountSid: String(payload?.twilioAccountSid ?? "").trim(),
      twilioAuthToken: String(payload?.twilioAuthToken ?? "").trim(),
      telnyxApiKey: String(payload?.telnyxApiKey ?? "").trim(),
      telnyxPublicKey: String(payload?.telnyxPublicKey ?? "").trim(),
      telnyxConnectionId: String(payload?.telnyxConnectionId ?? "").trim(),
    },
  };
}

export function normalizeBhgPhraseRules(input: unknown) {
  if (!Array.isArray(input)) {
    return null;
  }

  const normalized = input.map((entry) => {
    const payload = entry as Record<string, unknown>;
    return {
      id: String(payload.id ?? "").trim(),
      phrasePatterns: Array.isArray(payload.phrasePatterns)
        ? payload.phrasePatterns.map((value) => String(value ?? "").trim()).filter(Boolean)
        : [],
      verificationPhrases: Array.isArray(payload.verificationPhrases)
        ? payload.verificationPhrases.map((value) => String(value ?? "").trim()).filter(Boolean)
        : [],
      requiredIds: Array.isArray(payload.requiredIds)
        ? payload.requiredIds.map((value) => String(value ?? "").trim()).filter(Boolean)
        : [],
      resetIdsOnInvalid: Array.isArray(payload.resetIdsOnInvalid)
        ? payload.resetIdsOnInvalid.map((value) => String(value ?? "").trim()).filter(Boolean)
        : [],
      sendDigits: normalizeDialString(
        typeof payload.sendDigits === "string" ? payload.sendDigits : null
      ),
      sendDigitsWhenInvalid: normalizeDialString(
        typeof payload.sendDigitsWhenInvalid === "string" ? payload.sendDigitsWhenInvalid : null
      ),
      pauseSeconds:
        typeof payload.pauseSeconds === "number" && payload.pauseSeconds >= 0
          ? Math.floor(payload.pauseSeconds)
          : null,
      recordSeconds:
        typeof payload.recordSeconds === "number" && payload.recordSeconds >= 0
          ? Math.floor(payload.recordSeconds)
          : null,
      oneshot: Boolean(payload.oneshot ?? false),
      hangup: Boolean(payload.hangup ?? false),
      priority: Number(payload.priority ?? 0),
    } satisfies BhgPhraseTrigger;
  });

  if (normalized.some((rule) => !rule.id || rule.phrasePatterns.length === 0)) {
    return null;
  }

  return normalized;
}

export function normalizeBhgResultRules(input: unknown) {
  if (!Array.isArray(input)) {
    return null;
  }

  const normalized = input.map((entry) => {
    const payload = entry as Record<string, unknown>;
    const offsetNDaysRaw = payload.offsetNDays;
    const offsetNDays =
      typeof offsetNDaysRaw === "number" && Number.isFinite(offsetNDaysRaw)
        ? Math.trunc(offsetNDaysRaw)
        : undefined;
    return {
      id: String(payload.id ?? "").trim(),
      label: String(payload.label ?? "").trim(),
      phrasePatterns: Array.isArray(payload.phrasePatterns)
        ? payload.phrasePatterns.map((value) => String(value ?? "").trim()).filter(Boolean)
        : [],
      isScheduledTrigger:
        typeof payload.isScheduledTrigger === "boolean"
          ? payload.isScheduledTrigger
          : payload.scheduledCheckType === "drug_screen" ||
            payload.scheduledCheckType === "diversion_check",
      countsTowardMonthlyScreen:
        payload.countsTowardMonthlyScreen === true
          ? true
          : payload.countsTowardMonthlyScreen === false
            ? false
            : // Legacy payloads predate the flag: infer from the well-known key
              // so an admin save doesn't silently clear the drug-screen default.
              payload.id === "drug_screen" || payload.scheduledCheckType === "drug_screen",
      offsetNDays,
      isTechDifficultyAlert: payload.isTechDifficultyAlert === true ? true : undefined,
      priority: Number(payload.priority ?? 0),
    } satisfies BhgResultTrigger;
  });

  if (normalized.some((rule) => !rule.id || !rule.label || rule.phrasePatterns.length === 0)) {
    return null;
  }

  return normalized;
}

export async function loadBhgCallSettings(dbClient: ProductDbClient) {
  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin<{ settings: unknown }>({
      method: "GET",
      path: "/api/admin/bhg-call/runtime-settings",
    });
    if (response.ok) {
      const normalized = normalizeBhgCallSettings(response.data.settings);
      if (normalized.ok) {
        return normalized.settings;
      }
    }
  }

  const loaded = await dbClient.loadJson<unknown>(RUNTIME_SETTINGS_KEY);
  if (!loaded) {
    return DEFAULT_BHG_CALL_SETTINGS;
  }

  const normalized = normalizeBhgCallSettings(loaded);
  if (!normalized.ok) {
    return DEFAULT_BHG_CALL_SETTINGS;
  }

  return normalized.settings;
}

export async function loadBhgPhraseRules(dbClient: ProductDbClient) {
  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin<{ rules: unknown }>({
      method: "GET",
      path: "/api/admin/bhg-call/phrase-rules",
    });
    if (response.ok) {
      const normalized = normalizeBhgPhraseRules(response.data.rules);
      if (normalized) {
        return normalized;
      }
    }
  }

  const loaded = await dbClient.loadJson<unknown>(PHRASE_RULES_KEY);
  const normalized = normalizeBhgPhraseRules(loaded);
  return normalized ?? DEFAULT_BHG_PHRASE_TRIGGERS;
}

export async function loadBhgResultRules(dbClient: ProductDbClient) {
  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin<{ rules: unknown }>({
      method: "GET",
      path: "/api/admin/bhg-call/result-rules",
    });
    if (response.ok) {
      const normalized = normalizeBhgResultRules(response.data.rules);
      if (normalized) {
        return normalized;
      }
    }
  }

  const loaded = await dbClient.loadJson<unknown>(RESULT_RULES_KEY);
  const normalized = normalizeBhgResultRules(loaded);
  return normalized ?? DEFAULT_BHG_RESULT_TRIGGERS;
}

export async function loadBhgCallAdminSettings(dbClient: ProductDbClient) {
  const [runtimeSettings, phraseRules, resultRules] = await Promise.all([
    loadBhgCallSettings(dbClient),
    loadBhgPhraseRules(dbClient),
    loadBhgResultRules(dbClient),
  ]);

  return {
    runtimeSettings,
    phraseRules,
    resultRules,
  } satisfies BhgCallAdminSettings;
}

export async function listBhgActiveCallSids(
  dbClient: ProductDbClient
): Promise<{ ok: true; callSids: string[] } | { ok: false; error: string }> {
  if (!dbClient.requestAdmin) {
    return { ok: false, error: "Manual hangup requires admin API access." };
  }

  const response = await dbClient.requestAdmin<{ callSids?: unknown }>({
    method: "GET",
    path: "/api/admin/bhg-call/active-calls",
  });
  if (!response.ok) {
    return { ok: false, error: response.error };
  }

  const callSids = Array.isArray(response.data.callSids)
    ? response.data.callSids
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter((value) => value.length > 0)
    : [];

  return { ok: true, callSids };
}

export async function requestBhgManualHangup(
  callSidInput: string,
  dbClient: ProductDbClient
): Promise<{ ok: true; result: BhgManualHangupResult } | { ok: false; error: string }> {
  if (!dbClient.requestAdmin) {
    return { ok: false, error: "Manual hangup requires admin API access." };
  }

  const callSid = callSidInput.trim();
  if (!callSid) {
    return { ok: false, error: "Call ID is required." };
  }

  const response = await dbClient.requestAdmin<{ result?: unknown }>({
    method: "POST",
    path: "/api/admin/bhg-call/hangup-call",
    body: { callSid },
  });
  if (!response.ok) {
    return { ok: false, error: response.error };
  }

  const payload = response.data.result as Record<string, unknown> | undefined;
  const outcome = String(payload?.outcome ?? "").trim();
  const persistedStatus =
    payload?.persistedStatus === "completed" || payload?.persistedStatus === "failed"
      ? payload.persistedStatus
      : null;

  if (
    !payload ||
    typeof payload.callSid !== "string" ||
    (outcome !== "hangup_requested" && outcome !== "already_terminal" && outcome !== "not_found")
  ) {
    return { ok: false, error: "Manual hangup response was invalid." };
  }

  return {
    ok: true,
    result: {
      callSid: payload.callSid.trim(),
      outcome,
      persistedStatus,
      providerStatus: typeof payload.providerStatus === "string" ? payload.providerStatus : null,
    },
  };
}

export async function listBhgActiveDbCalls(
  dbClient: ProductDbClient
): Promise<{ ok: true; calls: ActiveDbCall[] } | { ok: false; error: string }> {
  if (!dbClient.requestAdmin) {
    return { ok: false, error: "Active DB calls require admin API access." };
  }

  const response = await dbClient.requestAdmin<{ calls?: unknown }>({
    method: "GET",
    path: "/api/admin/bhg-call/active-db-calls",
  });
  if (!response.ok) {
    return { ok: false, error: response.error };
  }

  const calls = Array.isArray(response.data.calls) ? response.data.calls : [];
  return {
    ok: true,
    calls: calls.map((call: Record<string, unknown>) => ({
      id: Number(call.id ?? 0),
      userId: Number(call.userId ?? 0),
      callSid: String(call.callSid ?? ""),
      status: String(call.status ?? ""),
      startedAt: String(call.startedAt ?? ""),
      voiceProvider: typeof call.voiceProvider === "string" ? call.voiceProvider : null,
      errorReason: typeof call.errorReason === "string" ? call.errorReason : null,
    })),
  };
}

export async function resolveStaleCall(
  callId: number,
  resolution: "completed" | "failed",
  dbClient: ProductDbClient
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!dbClient.requestAdmin) {
    return { ok: false, error: "Resolving calls requires admin API access." };
  }

  const response = await dbClient.requestAdmin({
    method: "POST",
    path: "/api/admin/bhg-call/resolve-call",
    body: { callId, resolution },
  });
  if (!response.ok) {
    return { ok: false, error: response.error };
  }
  return { ok: true };
}

export async function loadCallHistory(
  dbClient: ProductDbClient,
  userId?: number
): Promise<{ ok: true; calls: CallHistoryEntry[] } | { ok: false; error: string }> {
  if (!dbClient.requestAdmin) {
    return { ok: false, error: "Call history requires admin API access." };
  }

  const path = userId
    ? `/api/admin/bhg-call/call-history?userId=${userId}`
    : "/api/admin/bhg-call/call-history";

  const response = await dbClient.requestAdmin<{ calls?: unknown }>({
    method: "GET",
    path,
  });
  if (!response.ok) {
    return { ok: false, error: response.error };
  }

  const calls = Array.isArray(response.data.calls) ? response.data.calls : [];
  return {
    ok: true,
    calls: calls.map((call: Record<string, unknown>) => ({
      id: Number(call.id ?? 0),
      userId: Number(call.userId ?? 0),
      startedAt: String(call.startedAt ?? ""),
      status: String(call.status ?? ""),
      resultLabel: typeof call.resultLabel === "string" ? call.resultLabel : null,
      resultDate: typeof call.resultDate === "string" ? call.resultDate : null,
      callSid: String(call.callSid ?? ""),
      transcript: typeof call.transcript === "string" ? call.transcript : null,
      recordingAvailable: Boolean(call.recordingAvailable),
      errorReason: typeof call.errorReason === "string" ? call.errorReason : null,
      voiceProvider: typeof call.voiceProvider === "string" ? call.voiceProvider : null,
    })),
  };
}

export async function loadMergedActiveCalls(
  dbClient: ProductDbClient
): Promise<
  | { ok: true; entries: ActiveCallEntry[]; providerError: string | null }
  | { ok: false; error: string }
> {
  const [providerResult, dbResult] = await Promise.all([
    listBhgActiveCallSids(dbClient),
    listBhgActiveDbCalls(dbClient),
  ]);

  if (!dbResult.ok) {
    return { ok: false, error: dbResult.error };
  }

  const providerError = providerResult.ok ? null : providerResult.error;
  const providerSids = new Set(providerResult.ok ? providerResult.callSids : []);
  const dbCallsBySid = new Map<string, ActiveDbCall>();
  for (const call of dbResult.calls) {
    dbCallsBySid.set(call.callSid, call);
  }

  const allSids = new Set([...providerSids, ...dbCallsBySid.keys()]);
  const entries: ActiveCallEntry[] = [];

  for (const callSid of allSids) {
    entries.push({
      callSid,
      dbCall: dbCallsBySid.get(callSid) ?? null,
      providerActive: providerSids.has(callSid),
    });
  }

  entries.sort((a, b) => {
    const aTime = a.dbCall?.startedAt ?? "";
    const bTime = b.dbCall?.startedAt ?? "";
    return bTime.localeCompare(aTime);
  });

  return { ok: true, entries, providerError };
}

export async function saveBhgCallSettings(
  settings: BhgCallSettings,
  dbClient: ProductDbClient
): Promise<BhgSettingsSaveResult> {
  const normalized = normalizeBhgCallSettings(settings);
  if (!normalized.ok) {
    return { ok: false, error: normalized.error };
  }

  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/runtime-settings",
      body: normalized.settings,
    });
    if (!response.ok) {
      return { ok: false, error: response.error };
    }
  } else {
    await dbClient.saveJson(RUNTIME_SETTINGS_KEY, normalized.settings);
  }
  return { ok: true };
}

export async function saveBhgPhraseRules(
  rules: BhgPhraseTrigger[],
  dbClient: ProductDbClient
): Promise<BhgSettingsSaveResult> {
  const normalized = normalizeBhgPhraseRules(rules);
  if (!normalized) {
    return {
      ok: false,
      error: "Each phrase trigger needs an ID and at least one phrase pattern.",
    };
  }

  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/phrase-rules",
      body: normalized,
    });
    if (!response.ok) {
      return { ok: false, error: response.error };
    }
  } else {
    await dbClient.saveJson(PHRASE_RULES_KEY, normalized);
  }
  return { ok: true };
}

export async function saveBhgResultRules(
  rules: BhgResultTrigger[],
  dbClient: ProductDbClient
): Promise<BhgSettingsSaveResult> {
  const normalized = normalizeBhgResultRules(rules);
  if (!normalized) {
    return {
      ok: false,
      error: "Each result trigger requires an ID, label, and at least one phrase pattern.",
    };
  }

  if (dbClient.requestAdmin) {
    const response = await dbClient.requestAdmin({
      method: "POST",
      path: "/api/admin/bhg-call/result-rules",
      body: normalized,
    });
    if (!response.ok) {
      return { ok: false, error: response.error };
    }
  } else {
    await dbClient.saveJson(RESULT_RULES_KEY, normalized);
  }
  return { ok: true };
}

export async function saveBhgCallAdminSettings(
  settings: BhgCallAdminSettings,
  dbClient: ProductDbClient
): Promise<BhgSettingsSaveResult> {
  const runtimeResult = await saveBhgCallSettings(settings.runtimeSettings, dbClient);
  if (!runtimeResult.ok) {
    return runtimeResult;
  }

  const phraseResult = await saveBhgPhraseRules(settings.phraseRules, dbClient);
  if (!phraseResult.ok) {
    return phraseResult;
  }

  return saveBhgResultRules(settings.resultRules, dbClient);
}
